/**
 * Pinned-message reconciliation for the new sync engine
 * (`featureFlags.useNewSync`).
 *
 * Why this exists
 * ----------------
 * Under the legacy bootstrap, `loadChats()` (middleware/chats.ts) decrypted and
 * wrote every chat's pinned messages into the dedicated `pinnedMessages` Dexie
 * table. The pinned UI (`PinnedMessageList`) reads ONLY that table.
 *
 * The new sync engine never writes that table — it only ever DELETES from it
 * (chat removal cleanup). It does, however, carry the authoritative pinned ids
 * on the chat row as `chat.pinnedMessageIds`. So after logout/login the pinned
 * UI is empty even though the chat object knows exactly what is pinned.
 *
 * This module reconciles the local pinned state for a chat to EXACTLY match
 * `chat.pinnedMessageIds` (a set reconciliation, not append-only):
 *   - ensure each id is present in `pinnedMessages` with `isPinned: true` on the
 *     corresponding `messages` row (fetching + decrypting the body if missing),
 *   - prune any `pinnedMessages` row for the chat whose id is no longer pinned,
 *     clearing `isPinned` on its `messages` row.
 *
 * It is idempotent and cheap (a no-op when the local set already matches), and
 * gated by the caller on `featureFlags.useNewSync` so the legacy path is
 * untouched.
 *
 * Correctness under concurrency
 * ------------------------------
 * The reconcile is hooked fire-and-forget into `umbra_progressivelyLoadMessages`
 * and is also driven by live socket events, so multiple passes for the SAME chat
 * can be in flight at once and finish out of order. Two safeguards prevent a
 * stale pass from clobbering newer state:
 *   1. It re-reads `chat.pinnedMessageIds` FRESH from Dexie at the start (never
 *      trusts a snapshot that may already be stale), so the latest pass always
 *      reflects the latest persisted set.
 *   2. Passes for the same chatId are SERIALIZED (chained on a per-chat promise)
 *      so they cannot interleave their reads/writes, and the final state is the
 *      last pass's view of the (fresh) chat row.
 * The prune + ensure writes run inside a single Dexie transaction so the set
 * update is atomic.
 */

import type { MessageEntity } from "@/indexedDB/types";
import { db } from "@/indexedDB/db";
import { createLogger } from "@/utils/debug";
import { getChatFromLocalDB } from "@/indexedDB/services/chats";
import { getMessageByServerIdFromLocalDB } from "@/indexedDB/services/messages";
import { getPinnedMessagesByChatIdFromLocalDB } from "@/indexedDB/services/pinnedMessages";
// NOTE: circular import with `umbra_messages` (it imports `reconcilePinnedMessages`
// from here). Runtime-safe because the binding below is dereferenced only at
// CALL time inside async functions, never at module-eval time — so there is no
// temporal-dead-zone risk. Extracting the single-message fetch/decrypt helper to
// a leaf module would remove the cycle entirely but is a large, dependency-heavy
// refactor of `umbra_processParentMessage`; deferred as not worth the risk here.
import { umbra_processParentMessage } from "./umbra_messages";

const log = createLogger("PinnedReconcile");

/**
 * Resolve the body of a pinned message into a ready-to-store `MessageEntity`
 * marked `isPinned: true`, WITHOUT writing anything (no Dexie writes here so the
 * network fetch can run outside a transaction).
 *
 * Resolution order:
 *   1. If the message already exists in `messages`, reuse it (no network).
 *   2. Otherwise fetch + decrypt it by id via `umbra_processParentMessage`
 *      (the existing single-message get-by-id + decrypt path — we do NOT
 *      hand-roll decryption).
 *
 * Returns `{ message, wasLocal }` where `wasLocal` indicates the body was
 * already present in `messages` (so the row exists and only needs flagging,
 * vs. a freshly fetched body that must be SEEDED into `messages`). Returns
 * `undefined` if the body could not be obtained (fetch failed / deleted) —
 * callers treat that as "skip this id this pass" and retry next reconcile.
 */
async function resolvePinnedBody({
  chatId,
  eCSK,
  messageId,
  signal,
}: {
  chatId: string;
  eCSK: string;
  messageId: string;
  signal?: AbortSignal;
}): Promise<{ message: MessageEntity; wasLocal: boolean } | undefined> {
  if (!messageId) {
    return undefined;
  }

  // 1. Already local? Reuse it (cheap path — no network).
  const existing = await getMessageByServerIdFromLocalDB(messageId);
  if (existing) {
    return { message: { ...existing, isPinned: true }, wasLocal: true };
  }

  // 2. Otherwise fetch + decrypt by id. `umbra_processParentMessage` returns a
  //    ready-to-store MessageEntity (or undefined on failure/deleted). It does
  //    NOT persist the row — that's the caller's job (FIX 1).
  const fetched = await umbra_processParentMessage({
    messageId,
    chatId,
    eCSK,
    signal,
  });
  if (!fetched) {
    return undefined;
  }

  return { message: { ...fetched, isPinned: true }, wasLocal: false };
}

/**
 * Materialize a single pinned message locally: write it into BOTH the
 * `messages` table (as `isPinned: true`) and the `pinnedMessages` table.
 *
 * Reused by the live websocket pin handler (`pinnedMessageEventsHandler`) so a
 * live pin of an unloaded-history message also materializes the body instead of
 * silently no-op'ing when the row isn't already local.
 *
 * The writes run in a single transaction over `[db.messages, db.pinnedMessages]`
 * so both tables update atomically. The (possibly networked) body resolution
 * happens BEFORE the transaction is opened.
 *
 * @returns the materialized `MessageEntity`, or `undefined` if the body could
 *          not be obtained.
 */
export async function ensurePinnedMessageLocal({
  chatId,
  eCSK,
  messageId,
  signal,
}: {
  chatId: string;
  eCSK: string;
  messageId: string;
  signal?: AbortSignal;
}): Promise<MessageEntity | undefined> {
  const resolved = await resolvePinnedBody({ chatId, eCSK, messageId, signal });
  if (!resolved) {
    return undefined;
  }

  const { message } = resolved;

  // Persist as pinned in BOTH tables atomically. Re-query by SERVER id INSIDE
  // the txn so we don't clobber a richer concurrent row: if the canonical row
  // exists, update ONLY `isPinned` (preserving reactions / decryptionStatus /
  // edit state); only a genuinely-absent body is inserted from the resolved
  // fetch (the only safe insert path — avoids duplicate `messages` rows too).
  await db.transaction("rw", db.messages, db.pinnedMessages, async () => {
    const existing = await db.messages.where("id").equals(message.id).first();
    if (existing) {
      if (!existing.isPinned) {
        await db.messages.update(existing._clientId, { isPinned: true });
      }
      await db.pinnedMessages.put({ ...existing, isPinned: true });
    } else {
      await db.messages.put(message);
      await db.pinnedMessages.put(message);
    }
  });

  return message;
}

// Per-chat serialization: chain reconcile passes for the same chatId so
// concurrent/out-of-order passes cannot interleave their reads/writes and a
// stale pass cannot clobber newer state. Each chat's tail promise is the last
// scheduled run; new runs await it (swallowing its rejection) before starting.
const reconcileChains = new Map<string, Promise<void>>();

// Generation fence (FIX MED#1): a logout bumps this counter. Each reconcile run
// captures the generation SYNCHRONOUSLY at ENQUEUE time (in
// `reconcilePinnedMessages`, before the chain's microtask continuation can run)
// and re-checks it before its (possibly networked) body fetch, on every
// fetch-loop iteration, and again INSIDE the write transaction. A run that was
// enqueued in a prior session is therefore fenced — it aborts without writing
// even if the SAME chatId is recreated after a new login.
//
// Capturing at enqueue time (NOT at the start of `runReconcile`) is load-bearing:
// the chain schedules each run on a promise continuation, so a run enqueued in
// session A can START executing only after `clearReconcileChains()` (logout) has
// already bumped the generation — capturing inside `runReconcile` would read the
// NEW generation and never fence, the exact cross-session bleed the fence exists
// to stop. Clearing the chain Map alone is also NOT sufficient: an escaped
// in-flight run (already past the chat read, still fetching) would not be chained
// behind by a post-login reconcile for the same id, so it could write last and
// bleed an old session's fetched bodies/pinned rows into the new session.
let reconcileGeneration = 0;

/**
 * Clear all per-chat reconcile chains. Call on logout so the (tiny, bounded)
 * Map of per-chat tail promises doesn't carry a previous session's entries into
 * the next. The in-flight runs themselves are best-effort and harmless once the
 * local DB has been cleared; this just drops the tracking references.
 *
 * Also bumps the generation fence so any reconcile already enqueued/in flight
 * cannot write after this point (see `reconcileGeneration`).
 */
export function clearReconcileChains(): void {
  reconcileGeneration++;
  reconcileChains.clear();
}

/**
 * Reconcile the local pinned state for `chatId` to EXACTLY match the CURRENT
 * `chat.pinnedMessageIds` (re-read fresh from Dexie — the passed snapshot, if
 * any, is NOT trusted).
 *
 * Idempotent: running it twice with the same persisted state is a no-op. Safe to
 * call on every chat-open and after every message-page write under the new sync
 * engine. Passes for the same chat are serialized so they never clobber.
 *
 * Gate the call site on `featureFlags.useNewSync`.
 *
 * Accepts either a chat id (preferred) or a chat-like object — callers that
 * already hold the chat row can pass it; only its `id` is used to re-fetch the
 * authoritative current row.
 */
export async function reconcilePinnedMessages(
  chatOrId: string | { id: string },
  signal?: AbortSignal,
): Promise<void> {
  const chatId = typeof chatOrId === "string" ? chatOrId : chatOrId.id;
  if (!chatId) {
    return;
  }

  // Capture the generation SYNCHRONOUSLY at enqueue time (FIX MED#1). Doing this
  // here — not inside `runReconcile`, which only executes on a later promise
  // continuation — guarantees a run enqueued in this session is fenced if a
  // logout bumps the generation before the chain reaches it.
  const scheduledGen = reconcileGeneration;

  const prev = reconcileChains.get(chatId) ?? Promise.resolve();
  const run = prev
    // Swallow the predecessor's failure so a single failed pass doesn't break
    // the chain for every later pass.
    .catch(() => undefined)
    .then(() => runReconcile(chatId, scheduledGen, signal));

  // Track the tail so the next pass chains after this one. Clean up when this is
  // the last queued run to avoid leaking entries for inactive chats.
  reconcileChains.set(chatId, run);
  void run
    .catch((err) => {
      // Observability (FIX C): the reconcile is hooked fire-and-forget, so a
      // failure would otherwise vanish silently. Log with the chatId so a
      // stuck/empty pinned panel is diagnosable.
      log.warn("pinned reconcile failed", { chatId, err });
    })
    .finally(() => {
      if (reconcileChains.get(chatId) === run) {
        reconcileChains.delete(chatId);
      }
    });

  return run;
}

async function runReconcile(
  chatId: string,
  // The generation captured SYNCHRONOUSLY at enqueue time (FIX MED#1) — NOT
  // re-read here. `runReconcile` runs on a promise continuation that may execute
  // after a logout bumped `reconcileGeneration`, so capturing now would defeat
  // the fence. All `gen !== reconcileGeneration` checks below use this value.
  gen: number,
  signal?: AbortSignal,
): Promise<void> {
  if (signal?.aborted) {
    return;
  }

  // Read the LIVE chat row fresh — never trust a possibly-stale snapshot. An
  // older fire-and-forget pass must not act on outdated `pinnedMessageIds`.
  const chat = await getChatFromLocalDB(chatId);
  if (!chat) {
    return;
  }

  // --- Resolve every desired body OUTSIDE the transaction --------------------
  // Body resolution may hit the network (fetch + decrypt), which cannot run
  // inside a Dexie transaction. Resolve against the PRE-txn snapshot of the
  // desired set; the authoritative prune/ensure decision is re-made INSIDE the
  // txn against a fresh re-read of `chat.pinnedMessageIds`, so a body we resolve
  // here but that is no longer pinned by then is simply not written.
  //
  // We keep the resolved bodies keyed by SERVER id (`fetchedById`). Inside the
  // txn we re-query each desired message by server id: if the canonical row is
  // already local we update ONLY `isPinned: true` (never `put` the whole entity,
  // which would clobber concurrently-updated reactions / decryptionStatus / edit
  // state); only a genuinely-absent body is inserted from `fetchedById`.
  // Fence (a): bail before the network body-fetch if a logout happened (FIX
  // MED#1). An escaped run that already read the chat row above must not go on
  // to fetch + write into a new session.
  if (gen !== reconcileGeneration) {
    log.info("reconcile fenced before fetch (logout)", { chatId });
    return;
  }

  const preTxnDesired = new Set(chat.pinnedMessageIds ?? []);
  const fetchedById = new Map<string, MessageEntity>();
  for (const messageId of preTxnDesired) {
    // Fence per iteration (FIX MED#2): a logout mid-loop must stop further
    // fetches and prevent stale mutations of `fetchedById` from a prior session.
    if (signal?.aborted || gen !== reconcileGeneration) {
      return;
    }
    // A permanently-unavailable pinned id simply causes one wasted fetch per
    // chat-open (mild waste, NOT a correctness issue — reconcile only runs on
    // chat-open, not in a hot loop). Intentional simplification over a fragile
    // per-session retry counter, which mis-handled temporarily-unfetchable pins.
    const resolved = await resolvePinnedBody({
      chatId,
      eCSK: chat.eCSK,
      messageId,
      signal,
    });
    // Re-check immediately AFTER the await (FIX MED#2): the fetch may have spanned
    // a logout — do not mutate loop/module state with a prior-session body.
    if (signal?.aborted || gen !== reconcileGeneration) {
      return;
    }
    if (resolved) {
      fetchedById.set(messageId, resolved.message);
    }
  }

  if (signal?.aborted) {
    return;
  }

  // --- Atomic set update -----------------------------------------------------
  // Prune rows no longer pinned + ensure desired rows, in ONE transaction over
  // both tables so a reader never observes a half-applied set. The desired set
  // is RE-READ fresh inside the txn (TOCTOU-safe): the network resolution above
  // ran on a snapshot, but the authoritative final state is decided here.
  await db.transaction(
    "rw",
    db.chats,
    db.messages,
    db.pinnedMessages,
    async () => {
      // Fence (b): re-check INSIDE the txn before ANY write (FIX MED#1). This is
      // the last gate — if a logout bumped the generation after the body fetch,
      // abort here so no stale-session row is written. (The txn is a no-op when
      // it returns without writing.)
      if (gen !== reconcileGeneration) {
        log.info("reconcile fenced before write (logout)", { chatId });
        return;
      }

      const freshChat = await db.chats.get(chatId);
      if (!freshChat) {
        return;
      }
      const desiredSet = new Set(freshChat.pinnedMessageIds ?? []);

      // Prune: any locally-pinned row no longer in the desired set. Covers the
      // "unpinned while offline" case — without this an unpinned message would
      // stay visible in the pinned panel forever.
      const localPinned = await getPinnedMessagesByChatIdFromLocalDB(chatId);
      for (const row of localPinned) {
        if (!desiredSet.has(row.id)) {
          await db.pinnedMessages.delete(row.id);
          // Clear the flag on the canonical messages row (no-op if not local).
          const msgRow = await db.messages.where("id").equals(row.id).first();
          if (msgRow) {
            await db.messages.update(msgRow._clientId, { isPinned: false });
          }
        }
      }

      // Ensure: every desired id is present + flagged + body local.
      for (const messageId of desiredSet) {
        // Re-query by SERVER id inside the txn (authoritative current row).
        const existing = await db.messages
          .where("id")
          .equals(messageId)
          .first();

        if (existing) {
          // Already-local path: update ONLY the pinned flag. Do NOT `put` the
          // whole entity — that would clobber a richer concurrent row (reactions,
          // decryptionStatus, edit state).
          if (!existing.isPinned) {
            await db.messages.update(existing._clientId, { isPinned: true });
          }
          await db.pinnedMessages.put({ ...existing, isPinned: true });
        } else {
          // Genuinely absent: insert the freshly-fetched body (the only safe
          // insert path — no canonical row exists to clobber). Skip if the body
          // could not be resolved this pass (retried next reconcile).
          const fetched = fetchedById.get(messageId);
          if (fetched) {
            await db.messages.put(fetched);
            await db.pinnedMessages.put(fetched);
          }
        }
      }
    },
  );
}
